import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  RefreshCw,
  GitBranch,
} from 'lucide-react';
import { mojaloopApi } from '../services/api';
import Pagination from '../components/Pagination';
import { formatDate, debounce, failureReason } from '../utils/helpers';

/** The outcomes that have something to explain. */
const FAILED_STATUSES = ['FAILED', 'EXPIRED'];

const STATUS_BADGE = {
  RECEIVED: 'badge-success',
  PENDING:  'badge-warning',
  EXPIRED:  'badge-info',
  FAILED:   'badge-danger',
};

const Quotes = () => {
  const [quotes, setQuotes]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filters, setFilters]       = useState({ status: '', search: '' });
  const [page, setPage]             = useState(0);
  const [pageSize, setPageSize]     = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const loadQuotes = useCallback(async (pg = page, sz = pageSize) => {
    try {
      setLoading(true);
      const data = await mojaloopApi.getQuotes({
        status: filters.status,
        search: filters.search,
        page: pg,
        size: sz,
      });
      console.log('[Quotes] raw page response:', data);
      console.log('[Quotes] first record sample:', data.content?.[0]);
      setQuotes(data.content    ?? []);
      setTotalItems(data.totalItems ?? 0);
      setTotalPages(data.totalPages ?? 0);
      setPage(data.page ?? 0);
    } catch (err) {
      console.error('[Quotes] load error:', err);
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(0); loadQuotes(0, pageSize); }, [filters]); // eslint-disable-line
  useEffect(() => { loadQuotes(page, pageSize); }, [page, pageSize]);   // eslint-disable-line

  const handleSearchChange = debounce((value) => {
    setFilters(prev => ({ ...prev, search: value }));
  }, 300);

  const handlePageChange     = (p) => setPage(p - 1);
  const handlePageSizeChange = (s) => { setPageSize(s); setPage(0); };
  const clearFilters         = () => setFilters({ status: '', search: '' });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Quotes</h1>
          <p className="text-gray-600 mt-1">FSPIOP /quotes records</p>
        </div>
        <button onClick={() => loadQuotes(page, pageSize)} disabled={loading} className="btn-primary flex items-center space-x-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          <button onClick={clearFilters} className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            Clear All
          </button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Quote ID, E2E ID, Payer FSP, Payee FSP, Party ID…"
                  className="input pl-10"
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                className="select"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="RECEIVED">Received</option>
                <option value="PENDING">Pending</option>
                <option value="EXPIRED">Expired</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">
            Results{totalItems > 0 ? ` (${totalItems.toLocaleString()})` : ''}
          </h2>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
                <p className="text-gray-600">Loading quotes…</p>
              </div>
            </div>
          ) : quotes.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <GitBranch className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">No quotes found</p>
                <p className="text-gray-600">Try adjusting your filters</p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['ID', 'Quote ID', 'Payer FSP', 'Payer Party', 'Payee FSP', 'Payee Party', 'Amount', 'Type', 'Status', 'Created'].map((h) => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {quotes.map((q) => (
                      <tr key={q.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-medium text-gray-900">
                          #{q.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          <span title={q.quoteId}>{q.quoteId?.substring(0, 16)}…</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{q.payerFsp}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900">{q.payerIdValue || <span className="text-gray-400">—</span>}</div>
                          {q.payerIdType && (
                            <div className="text-xs text-gray-400 mt-0.5">{q.payerIdType}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {q.payeeFsp || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-mono text-gray-900">{q.payeeIdValue || <span className="text-gray-400">—</span>}</div>
                          {q.payeeIdType && (
                            <div className="text-xs text-gray-400 mt-0.5">{q.payeeIdType}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {q.amount} <span className="text-gray-400 text-xs">{q.currency}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                            {q.amountType || 'SEND'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`badge ${STATUS_BADGE[q.status] || 'badge-info'}`}>
                            {q.status}
                          </span>
                          {/* Why it ended this way: the Hub's error, or why it never got there. */}
                          {FAILED_STATUSES.includes(q.status) && failureReason(q.callbackPayload) && (
                            <p className="mt-1 max-w-xs truncate text-xs text-gray-500"
                               title={failureReason(q.callbackPayload)}>
                              {failureReason(q.callbackPayload)}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(q.createdAt, 'PPp')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={page + 1}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 20, 50, 100]}
                loading={loading}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Quotes;
